<script setup lang="ts">
import type { ProductReviewFragment, ProductReviewsQuery } from '#graphql-operations'
import { SortOrder } from '#graphql-operations'

const props = defineProps<{
  reviewRating: number
  reviewCount: number
  productId: string
  productVariantId?: string | number
}>()

const { t } = useI18n()
const productId = computed(() => props.productId)

// Sort and filter state
const sortOption = ref<string>('totalAssets') // 'newest', 'recommended', 'highest', 'lowest'
const selectedRating = ref<number | string>('all')
const showWithMediaOnly = ref(false)

// Pagination state
const skip = ref(0)
const take = 5
const allReviews = ref<ProductReviewFragment[]>([])
const totalItems = ref(0)
const isLoading = ref(false)
const hasMore = computed(() => allReviews.value.length < totalItems.value)

// First 3 reviews from initial load
const initialPreviewReviews = ref<ProductReviewFragment[]>([])

const sortConfig = computed(() => {
  switch (sortOption.value) {
    case 'newest': {
      return { field: 'createdAt', order: SortOrder.DESC }
    }
    case 'highest': {
      return { field: 'rating', order: SortOrder.DESC }
    }
    case 'lowest':{
      return { field: 'rating', order: SortOrder.ASC }
    }
    case 'totalAssets': {
      return { field: 'totalAssets', order: SortOrder.DESC }
    }
    case 'recommended':
    default: {
      return { field: 'upvotes', order: SortOrder.DESC }
    }
  }
})

// Fetch reviews
async function fetchReviews(reset = false) {
  if (isLoading.value)
    return

  isLoading.value = true

  if (reset) {
    skip.value = 0
    allReviews.value = []
  }

  try {
    const { data } = await useGraphqlQuery('productReviews', {
      id: props.productId,
      options: {
        skip: skip.value,
        take,
        sort: {
          [sortConfig.value.field]: sortConfig.value.order,
        },
        ...((selectedRating.value !== 'all' || showWithMediaOnly.value) && {
          filter: {
            _and: [
              ...(selectedRating.value !== 'all'
                ? [{ rating: { eq: Number(selectedRating.value) } }]
                : []),
              ...(showWithMediaOnly.value
                ? [{ totalAssets: { gt: 0 } }]
                : []),
            ].filter(f => f), // Remove empty filters
          },
        }),
      },
    }) as { data: ProductReviewsQuery }

    const newReviews = data.product?.reviews?.items || []
    totalItems.value = data.product?.reviews?.totalItems || 0

    if (reset) {
      allReviews.value = newReviews
    }
    else {
      allReviews.value = [...allReviews.value, ...newReviews]
    }

    skip.value += newReviews.length
  }
  finally {
    isLoading.value = false
  }
}

// Load more function
function loadMore() {
  if (hasMore.value && !isLoading.value) {
    fetchReviews()
  }
}

// Intersection observer for infinite scroll
const loadMoreEl = useTemplateRef('load-more')

watchEffect((onCleanup) => {
  const el = loadMoreEl.value
  if (el) {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) {
        loadMore()
      }
    }, {
      rootMargin: '300px',
      threshold: [0],
    })
    observer.observe(el)
    onCleanup(() => {
      observer.disconnect()
    })
  }
})

// Helper functions
function clearAllFilters() {
  selectedRating.value = 'all'
  showWithMediaOnly.value = false
}

const hasActiveFilters = computed(() => selectedRating.value !== 'all' || showWithMediaOnly.value)

// Scroll container ref for dialog
const dialogScrollContainer = useTemplateRef('dialog-scroll-container')

// Reset when sort or filter changes
watch([sortOption, selectedRating, showWithMediaOnly], () => {
  if (dialogScrollContainer.value) {
    dialogScrollContainer.value.scrollTop = 0
  }
  fetchReviews(true)
})

// Initial load
onMounted(async () => {
  if (import.meta.client && productId.value) {
    await fetchReviews(true)
    // Save the first 3 reviews from initial unfiltered load
    initialPreviewReviews.value = allReviews.value.slice(0, 3)
  }
})
</script>

<template>
  <UiCard class="wfull space-y-8">
    <UiCardHeader :class="{ 'border-b': allReviews.length > 0 }">
      <div v-if="reviewRating && reviewCount" class="mb4 space-y-4">
        <div class="flex text-lg fw600 divide-x divide-border">
          <span class="mr2">{{ reviewCount }} {{ t('product.reviews.label') }}</span>

          <div class="flex items-center">
            <span class="mr2 pl2">{{ reviewRating }}</span>
            <Icon
              v-for="n in 5" :key="n"
              :name="n <= Math.floor(reviewRating) ? 'ph:star-fill' : 'ph:star-duotone'"
              class="c-amber4"
            />
          </div>
        </div>

        <ContentTip icon="i-ri:shield-user-fill">
          <span class="text-xs md:text-sm">{{ t('product.review_verified_purchases') }}</span>
        </ContentTip>
      </div>

      <div v-else class="flex flex-col items-center text-sm">
        <span class="fw600">
          {{ t('general.no_reviews.label') }}
        </span>
        <span class="text-muted-foreground">
          {{ t('product.no_reviews.description') }}
        </span>
      </div>

      <ProductReviewCreateModal :product-id :product-variant-id />
    </UiCardHeader>

    <UiCardContent v-if="reviewCount" class="!mt0">
      <!-- show only 3 reviews -->
      <template v-if="isLoading && initialPreviewReviews.length === 0">
        <ProductReviewListItemSkeleton v-for="i in 3" :key="`skeleton-${i}`" />
      </template>
      <ProductReviewList
        v-else
        class="mt0"
        :reviews="initialPreviewReviews"
      />

      <UiDialog>
        <div class="text-center">
          <UiDialogTrigger as-child>
            <UiButton
              v-if="reviewCount > 3 && initialPreviewReviews.length > 0"
              :disabled="isLoading"
              variant="outline"
              class="mt4 rounded-full"
            >
              {{ t('product_reviews.show_all') }}
            </UiButton>
          </UiDialogTrigger>
        </div>
        <UiDialogContent class="max-h-[90dvh] max-w-xl flex flex-col p-0">
          <div class="relative">
            <UiDialogHeader class="p-6 pb-4">
              <UiDialogTitle>
                {{ t('product_reviews.all_reviews') }}
              </UiDialogTitle>

              <!-- Filters on single row that wraps -->
              <div class="mt-4 flex flex-wrap items-center gap-2">
                <!-- Sort dropdown -->
                <UiSelect v-model="sortOption">
                  <UiSelectTrigger class="h-8 w-[160px]">
                    <UiSelectValue :placeholder="t('product_reviews.sort.placeholder')" />
                  </UiSelectTrigger>
                  <UiSelectContent>
                    <UiSelectItem value="recommended">
                      {{ t('product_reviews.sort.recommended') }}
                    </UiSelectItem>
                    <UiSelectItem value="newest">
                      {{ t('product_reviews.sort.newest') }}
                    </UiSelectItem>
                    <UiSelectItem value="highest">
                      {{ t('product_reviews.sort.highest_rated') }}
                    </UiSelectItem>
                    <UiSelectItem value="lowest">
                      {{ t('product_reviews.sort.lowest_rated') }}
                    </UiSelectItem>
                  </UiSelectContent>
                </UiSelect>

                <!-- Divider -->
                <UiSeparator orientation="vertical" class="h-6" />

                <!-- Rating select -->
                <UiSelect v-model="selectedRating">
                  <UiSelectTrigger class="h-8 w-[140px]">
                    <UiSelectValue :placeholder="t('product_reviews.filter.all_stars')" />
                  </UiSelectTrigger>
                  <UiSelectContent>
                    <UiSelectItem value="all">
                      {{ t('product_reviews.filter.all_stars') }}
                    </UiSelectItem>
                    <UiSelectItem v-for="stars in [5, 4, 3, 2, 1]" :key="stars" :value="stars">
                      <div class="flex items-center gap-1">
                        <span>{{ stars }}</span>
                        <Icon name="ph:star-fill" class="h-3.5 w-3.5 c-amber4" />
                      </div>
                    </UiSelectItem>
                  </UiSelectContent>
                </UiSelect>

                <!-- With media toggle -->
                <UiButton
                  size="sm"
                  :variant="showWithMediaOnly ? 'default' : 'outline'"
                  class="h-8"
                  @click="showWithMediaOnly = !showWithMediaOnly"
                >
                  <Icon name="i-heroicons:photo" class="mr-1.5 h-4 w-4" />
                  {{ t('product_reviews.filter.with_media') }}
                </UiButton>

                <!-- Clear filters (shown when filters active) -->
                <UiButton
                  v-if="hasActiveFilters"
                  size="sm"
                  variant="ghost"
                  class="ml-auto h-8"
                  @click="clearAllFilters"
                >
                  <Icon name="i-heroicons:x-mark" class="mr-1.5 h-4 w-4" />
                  {{ t('product_reviews.filter.clear_all') }}
                </UiButton>
              </div>
            </UiDialogHeader>

            <div class="pointer-events-none absolute inset-x-0 z-10 h-12 from-background to-transparent from-50% bg-gradient-to-b" />
          </div>

          <div ref="dialog-scroll-container" class="grid gap-4 overflow-y-auto px-6 py-4">
            <div class="flex flex-col justify-between">
              <template v-if="isLoading && allReviews.length === 0">
                <ProductReviewListItemSkeleton
                  v-for="i in 3"
                  :key="`skeleton-${i}`"
                />
              </template>
              <ProductReviewList
                v-else
                :reviews="allReviews"
              />
              <div v-if="hasMore" ref="load-more" class="flex justify-center">
                <svg class="h-4 w-4 animate-spin text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
            </div>
          </div>
        </UiDialogContent>
      </UiDialog>
    </UiCardContent>
  </UiCard>
</template>
